import React, { useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, Loader2, Search } from 'lucide-react';
import type { Ingredient } from './IngredientList';
import { normalizeIngredientUnitKey } from '@/utils/ingredient-units';
import { formatMacro } from '@/utils/gram-ml';
import { isGramUnit, shouldShowDistinctMlValue, toPositiveNumber, gramsToMl } from '@/utils/gram-ml';
import { cn } from '@/lib/utils';
import { RecipeAllergenCard } from '@/components/recipes/RecipeAllergenCard';
import { SpoonacularTagSummary } from '@/components/recipes/SpoonacularTagSummary';
import { Tag } from '@/components/ui/tag';

const ML_UNIT_PATTERN = /^(ml|milliliter|milliliters|millilitre|millilitres)$/i;

function isMlUnit(unit: string): boolean {
  return ML_UNIT_PATTERN.test(unit.trim());
}

function formatFactorPercent(value: unknown): string | null {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  const percent = numeric * 100;
  return `${Number.isInteger(percent) ? percent : Number(percent.toFixed(1))}%`;
}

const mergeTagArrays = (...values: unknown[]): string[] => {
  const merged: string[] = [];
  const seen = new Set<string>();

  for (const value of values) {
    const entries = Array.isArray(value) ? value : value ? [value] : [];
    for (const entry of entries) {
      const normalized = String(entry ?? '').trim();
      const key = normalized.toLowerCase();
      if (!normalized || seen.has(key)) continue;
      seen.add(key);
      merged.push(normalized);
    }
  }

  return merged;
};

export interface IngredientSearchResultItem {
  id: string;
  name: string;
  description?: string | null;
  image?: string;
  imageUrl?: string;
  aisle?: string;
  possibleUnits?: string[];
  allergens?: string[];
  allergenTagIds?: string[];
  dietaryTagIds?: string[];
  dietLabels?: string[];
  intoleranceTagIds?: string[];
  intoleranceTags?: string[];
  tagClassificationStatus?: 'not_cached' | 'queued' | 'processing' | 'completed' | 'failed';
  tagClassificationSource?: 'preview' | 'ai' | 'none';
  tagClassificationUpdatedAt?: string | null;
  source?: string;
  sourceId?: string;
  provider?: string;
  spoonacularId?: string;
  yieldFactor?: number | null;
  retentionFactor?: number | null;
}

interface IngredientFormProps {
  ingredientSearchQuery: string;
  selectedIngredientName: string;
  selectedIngredientPreviewUrl?: string;
  selectedYieldFactor?: number | null;
  selectedRetentionFactor?: number | null;
  ingredientAmount: string;
  ingredientUnit: string;
  unitWeightGrams: string;
  unitWeightMl: string;
  effectiveAmountGrams: number | null;
  effectiveAmountMl: number | null;
  gramsPerMl: number | null;
  availableUnits: string[];
  unitLabelsByKey: Record<string, string>;
  unitWeightsByKey: Record<string, number>;
  nutritionData: Ingredient['nutritionData'] | null;
  isLoadingNutrition: boolean;
  isLoadingUnits: boolean;
  isEditing: boolean;
  showIngredientResults: boolean;
  ingredientSearchResults: IngredientSearchResultItem[];
  isSearchingIngredients: boolean;
  onSearchQueryChange: (value: string) => void;
  onIngredientSelect: (item: IngredientSearchResultItem) => void;
  onAmountChange: (value: string) => void;
  onUnitChange: (value: string) => void;
  onUnitWeightChange: (value: string) => void;
  onUnitWeightMlChange: (value: string) => void;
  onCloseDropdown: () => void;
  onSubmit: (options?: { continueAdding?: boolean }) => void;
  onCancel: () => void;
}

function Thumbnail({
  imageUrl,
  name,
  size = 'sm',
}: {
  imageUrl?: string;
  name: string;
  size?: 'sm' | 'lg';
}) {
  const dim = size === 'sm' ? 'size-[44px]' : 'size-20';
  return (
    <div
      className={cn(
        'shrink-0 overflow-hidden rounded-[14px] border border-[#f3f4f6] bg-white p-0.5 shadow-sm',
        size === 'sm' ? 'h-[44px] w-[44px] min-w-[44px]' : 'h-20 w-20 min-w-[80px]',
      )}
    >
      {imageUrl ? (
        <img src={imageUrl} alt="" className="h-full w-full object-contain rounded-[12px]" />
      ) : (
        <div className="flex h-full w-full items-center justify-center rounded-[12px] bg-[#f3f4f6] text-sm font-semibold text-[#99a1af]">
          {name.trim().slice(0, 1).toUpperCase() || '?'}
        </div>
      )}
    </div>
  );
}

function MacroStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-normal leading-[16.5px] tracking-[0.0645px] text-[#9ca3af]">
        {label}
      </span>
      <span className="text-[13px] font-semibold leading-[19.5px] tracking-[-0.0762px] text-[#0a0a0a]">
        {value}
      </span>
    </div>
  );
}

function IngredientDetailsExpandCard({
  imageUrl,
  name,
  description,
  nutrition,
  isLoadingNutrition = false,
}: {
  imageUrl?: string;
  name: string;
  description: string;
  nutrition: Ingredient['nutritionData'] | null;
  isLoadingNutrition?: boolean;
}) {
  return (
    <div
      className="flex gap-4 rounded-[14px] border border-[#e0f7fb] bg-[#eefbff] px-[17px] py-4"
      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      <div className="relative h-20 w-20 min-w-[80px] shrink-0 overflow-hidden rounded-[14px] border border-[#a5f3fc] bg-white p-0.5">
        {imageUrl ? (
          <img src={imageUrl} alt="" className="h-full w-full rounded-[12px] object-contain object-center" />
        ) : (
          <div className="flex h-full w-full items-center justify-center rounded-[12px] bg-[#ecfeff] text-xl font-semibold text-[#22d3ee]">
            {name.trim().slice(0, 1).toUpperCase() || '?'}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-normal leading-[18px] text-[#0e7490]">{description}</p>
        <div className="mt-3 flex flex-col gap-3 min-[480px]:flex-row min-[480px]:items-end min-[480px]:justify-between">
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            {isLoadingNutrition ? (
              <div className="flex h-[38px] items-center">
                <Loader2 className="size-5 animate-spin text-[#0e7490]" aria-hidden />
                <span className="sr-only">Loading nutrition</span>
              </div>
            ) : nutrition ? (
              <>
                <MacroStat label="Calories" value={`${formatMacro(nutrition.calories)} kcal`} />
                <MacroStat label="Protein" value={`${formatMacro(nutrition.macros.protein)}g`} />
                <MacroStat label="Carbs" value={`${formatMacro(nutrition.macros.carbs)}g`} />
                <MacroStat label="Fat" value={`${formatMacro(nutrition.macros.fat)}g`} />
              </>
            ) : (
              <>
                <MacroStat label="Calories" value="—" />
                <MacroStat label="Protein" value="—" />
                <MacroStat label="Carbs" value="—" />
                <MacroStat label="Fat" value="—" />
              </>
            )}
          </div>
          {/*
          <div className="flex shrink-0 flex-wrap justify-start gap-2 min-[480px]:justify-end">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center justify-center rounded-full border border-[#cfd9dd] bg-white px-2.5 py-0.5 text-xs font-semibold leading-[15px] tracking-[0.1172px] text-[#0e7490]"
              >
                {tag}
              </span>
            ))}
          </div>
          */}
        </div>
      </div>
    </div>
  );
}

export const IngredientForm: React.FC<IngredientFormProps> = ({
  ingredientSearchQuery,
  selectedIngredientName,
  selectedIngredientPreviewUrl,
  selectedYieldFactor,
  selectedRetentionFactor,
  ingredientAmount,
  ingredientUnit,
  unitWeightGrams,
  unitWeightMl,
  effectiveAmountGrams,
  effectiveAmountMl,
  gramsPerMl,
  availableUnits,
  unitLabelsByKey,
  unitWeightsByKey,
  nutritionData,
  isLoadingNutrition,
  isLoadingUnits,
  isEditing,
  ingredientSearchResults,
  isSearchingIngredients,
  onSearchQueryChange,
  onIngredientSelect,
  onAmountChange,
  onUnitChange,
  onUnitWeightChange,
  onUnitWeightMlChange,
  onSubmit,
  onCancel,
}) => {
  const getUnitDisplayLabel = useCallback(
    (unit: string) => unitLabelsByKey[normalizeIngredientUnitKey(unit)] ?? unit,
    [unitLabelsByKey],
  );

  const usesVolumeUnit = isMlUnit(ingredientUnit);
  const usesCollapsedUnitLayout = isGramUnit(ingredientUnit) || usesVolumeUnit;
  const currentUnitGrams =
    toPositiveNumber(unitWeightGrams) ??
    toPositiveNumber(unitWeightsByKey[normalizeIngredientUnitKey(ingredientUnit)]);
  const currentUnitMl =
    toPositiveNumber(unitWeightMl) ?? gramsToMl(currentUnitGrams, gramsPerMl);
  const canShowUnitMl = shouldShowDistinctMlValue(currentUnitGrams, currentUnitMl);
  const canShowTotalMl = shouldShowDistinctMlValue(effectiveAmountGrams, effectiveAmountMl);

  const resultCount = ingredientSearchResults.length;
  const showResultCount =
    !selectedIngredientName.trim() &&
    ingredientSearchQuery.trim().length >= 2 &&
    !isSearchingIngredients;

  const canSubmit =
    Boolean(selectedIngredientName.trim()) &&
    Boolean(ingredientAmount) &&
    Boolean(ingredientUnit) &&
    !isLoadingNutrition &&
    !isLoadingUnits;

  const kcalDisplay = useMemo(() => {
    const k = nutritionData?.calories;
    if (typeof k !== 'number' || !Number.isFinite(k)) return null;
    return `${formatMacro(k)} kcal`;
  }, [nutritionData]);

  const selectedFactorChips = useMemo(() => {
    const retention = formatFactorPercent(selectedRetentionFactor);
    const yieldValue = formatFactorPercent(selectedYieldFactor);
    return [
      retention ? `Retention ${retention}` : null,
      yieldValue ? `Yield ${yieldValue}` : null,
    ].filter((label): label is string => Boolean(label));
  }, [selectedRetentionFactor, selectedYieldFactor]);

  const handleSubmit = useCallback(() => {
    onSubmit({ continueAdding: true });
  }, [onSubmit]);

  return (
    <div
      className="flex max-h-[500px] flex-col gap-0 overflow-hidden rounded-2xl bg-white shadow-[0px_8px_20px_-4px_rgba(0,0,0,0.1),0px_4px_8px_-2px_rgba(0,0,0,0.06)] ring-1 ring-slate-200"
      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      <div className="shrink-0 border-b border-transparent px-5 pb-3 pt-4">
        <label className="flex h-[38px] cursor-text items-center gap-2 rounded-[14px] border border-[#e5e7eb] bg-white px-[13px] transition-colors focus-within:border-[#d1d5dc]">
          <Search className="pointer-events-none size-[14px] shrink-0 text-[#99a1af]" strokeWidth={2} aria-hidden />
          <input
            type="text"
            autoComplete="off"
            enterKeyHint="search"
            value={ingredientSearchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            placeholder="Search by Ingredients"
            aria-label="Search by ingredients"
            className="min-h-0 min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] tracking-[-0.0762px] text-[#1e2939] placeholder:text-[#99a1af] shadow-none outline-none ring-0 focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0"
          />
          {isSearchingIngredients ? (
            <Loader2 className="size-4 shrink-0 animate-spin text-[#20aedc]" aria-hidden />
          ) : null}
        </label>
      </div>

      <div className="shrink-0 px-5 pb-1">
        {showResultCount ? (
          <p className="mt-2 text-xs leading-[18px] text-[#9ca3af]">
            {resultCount === 0
              ? 'No ingredients found'
              : `${resultCount} Ingredients found`}
          </p>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-2">
        <div className="flex flex-col gap-4">
          {selectedIngredientName.trim() ? (
            <div className="flex flex-col gap-4">
              {/* Configuration Grid */}
              <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <h4 className="truncate text-sm font-semibold capitalize text-slate-900">{selectedIngredientName}</h4>
                    {selectedFactorChips.length > 0 ? (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {selectedFactorChips.map((label) => (
                          <Tag
                            key={label}
                            variant="outlined"
                            size="xs"
                            className="h-5 rounded-md bg-white px-1.5 font-medium text-slate-500 ring-1 ring-slate-200"
                          >
                            {label}
                          </Tag>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  {kcalDisplay ? (
                    <Tag variant="warning" size="sm" className="shrink-0 bg-orange-100 text-orange-600">
                      {kcalDisplay}
                    </Tag>
                  ) : null}
                </div>
                <div className={`grid grid-cols-1 ${usesCollapsedUnitLayout ? (usesVolumeUnit ? 'sm:grid-cols-3' : 'sm:grid-cols-2') : 'sm:grid-cols-4'} gap-4`}>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">
                      Quantity
                    </span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={ingredientAmount}
                      onChange={(e) => onAmountChange(e.target.value)}
                      onWheel={(e) => e.currentTarget.blur()}
                      placeholder="e.g. 1"
                      className="h-9 bg-white"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">
                      Unit
                    </span>
                    <Select
                      value={ingredientUnit}
                      onValueChange={onUnitChange}
                      disabled={isLoadingUnits}
                    >
                      <SelectTrigger className="h-9 bg-white">
                        <SelectValue placeholder={isLoadingUnits ? 'Loading...' : 'Select unit'} />
                      </SelectTrigger>
                      <SelectContent className="z-[200]" position="popper">
                        {availableUnits.length > 0 ? (
                          availableUnits.map((u) => (
                            <SelectItem key={u} value={u} className="text-xs">
                              {getUnitDisplayLabel(u)}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-units" disabled className="text-xs">
                            No units available
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  {!usesCollapsedUnitLayout ? (
                    <>
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-semibold text-slate-500">
                          Unit Grams
                        </span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={unitWeightGrams}
                          disabled
                          onChange={(e) => onUnitWeightChange(e.target.value)}
                          onWheel={(e) => e.currentTarget.blur()}
                          placeholder="g"
                          className="h-9 bg-slate-50 cursor-not-allowed [&.Mui-disabled]:opacity-100 [&_input:disabled]:[-webkit-text-fill-color:#64748B]"
                        />
                      </div>
                      {canShowUnitMl ? (
                        <div className="flex flex-col gap-1.5">
                          <span className="text-xs font-semibold text-slate-500">
                            Unit ML
                          </span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={unitWeightMl}
                            disabled
                            onChange={(e) => onUnitWeightMlChange(e.target.value)}
                            onWheel={(e) => e.currentTarget.blur()}
                            placeholder="ml"
                            className="h-9 bg-slate-50 cursor-not-allowed [&.Mui-disabled]:opacity-100 [&_input:disabled]:[-webkit-text-fill-color:#64748B]"
                          />
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
                {effectiveAmountGrams != null ? (
                  <p className="mt-3 text-xs text-slate-500">
                    Total Weight: <span className="font-medium text-slate-700">{Math.round(effectiveAmountGrams * 100) / 100}g</span>
                    {canShowTotalMl && effectiveAmountMl != null
                      ? ` / ${Math.round(effectiveAmountMl * 100) / 100}ml`
                      : ''}
                  </p>
                ) : effectiveAmountMl != null ? (
                  <p className="mt-3 text-xs text-slate-500">
                    Total Volume: <span className="font-medium text-slate-700">{Math.round(effectiveAmountMl * 100) / 100}ml</span>
                  </p>
                ) : null}
              </div>

              {/* Live Nutrition Preview */}
              <IngredientDetailsExpandCard
                imageUrl={selectedIngredientPreviewUrl}
                name={selectedIngredientName}
                description="Live nutrition preview for your selected quantity."
                nutrition={nutritionData}
                isLoadingNutrition={isLoadingNutrition}
              />
            </div>
          ) : (
            <section>
              {ingredientSearchResults.map((item) => {
                const id = String(item.id ?? item.name);
                const thumb =
                  typeof item.imageUrl === 'string'
                    ? item.imageUrl
                    : typeof item.image === 'string'
                      ? item.image
                      : undefined;
                const allergenTags = mergeTagArrays(item.allergens, item.allergenTagIds);
                const description = typeof item.description === 'string' ? item.description.trim() : '';
                return (
                  <div key={id} className="border-b border-[#f3f4f6] py-1 last:border-0">
                    <button
                      type="button"
                      onClick={() => onIngredientSelect(item)}
                      className="flex w-full items-start gap-3 px-3 py-2 text-left transition-colors hover:bg-slate-50"
                      aria-label={`Select ${item.name}`}
                    >
                      <Thumbnail imageUrl={thumb} name={item.name} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-semibold leading-4 tracking-[-0.0762px] text-[#1e2939]">
                          {item.name}
                        </p>
                        {item.aisle ? (
                          <p className="mt-0.5 truncate text-xs text-slate-500">{item.aisle}</p>
                        ) : null}
                        {description ? (
                          <p className="mt-0.5 line-clamp-2 text-xs leading-4 text-slate-500">
                            {description}
                          </p>
                        ) : null}
                        <RecipeAllergenCard
                          allergens={allergenTags}
                          compact
                          maxVisible={3}
                          className="mt-1"
                        />
                        <SpoonacularTagSummary item={item} className="mt-1" />
                        {formatFactorPercent(item.retentionFactor) || formatFactorPercent(item.yieldFactor) ? (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {formatFactorPercent(item.retentionFactor) ? (
                              <Tag variant="neutral" size="xs" className="h-5 rounded-md px-1.5 font-medium text-slate-500">
                                Ret {formatFactorPercent(item.retentionFactor)}
                              </Tag>
                            ) : null}
                            {formatFactorPercent(item.yieldFactor) ? (
                              <Tag variant="neutral" size="xs" className="h-5 rounded-md px-1.5 font-medium text-slate-500">
                                Yield {formatFactorPercent(item.yieldFactor)}
                              </Tag>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                      <div className="mt-1 flex h-7 items-center justify-center gap-1.5 rounded-full bg-[#eefbff] px-3 text-xs font-semibold text-[#0e7490] transition-colors">
                        Select
                      </div>
                    </button>
                  </div>
                );
              })}

              {!selectedIngredientName.trim() &&
              ingredientSearchResults.length === 0 &&
              !isSearchingIngredients ? (
                <div className="rounded-[14px] border border-dashed border-[#e5e7eb] bg-[#fafafa] px-4 py-8 text-center text-sm text-[#9ca3af]">
                  {ingredientSearchQuery.trim().length < 2
                    ? 'Type at least 2 characters to search ingredients.'
                    : 'No matches. Try a different search.'}
                </div>
              ) : null}
            </section>
          )}
        </div>
      </div>

      <div className="shrink-0 border-t border-[#f3f4f6] bg-slate-50 px-5 py-3">
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="h-[34px] rounded-[10px] border-[#e5e7eb] px-4 text-[13px] font-semibold text-[#374151] hover:bg-[#f9fafb]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="h-[34px] gap-1.5 rounded-[10px] border-0 bg-[#20aedc] px-3 text-[13px] font-medium text-white hover:bg-[#1b9bc4] disabled:opacity-50"
          >
            {isLoadingNutrition ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Check className="size-4" strokeWidth={2.5} />
            )}
            {isEditing ? 'Save Changes' : 'Add Ingredient'}
          </Button>
        </div>
      </div>
    </div>
  );
};
